/**
 * 高度な画像アップローダー - HEIC対応版
 * - HEIC/HEIF → JPEG変換
 * - クライサイド圧縮
 * - プログレス表示
 * - WebP対応
 * - 複数画像の並列処理
 */

// heic2anyライブラリは動的に読み込むため、importは削除
const piexifModule = require('./piexif');
// Webpack/ESM/CommonJSの互換性確保
const piexif = (function() {
    if (piexifModule && typeof piexifModule.load === 'function') return piexifModule;
    if (piexifModule && piexifModule.default && typeof piexifModule.default.load === 'function') return piexifModule.default;
    if (piexifModule && piexifModule.piexif && typeof piexifModule.piexif.load === 'function') return piexifModule.piexif;
    return window.piexif;
})();

console.log('DEBUG: piexif resolution:', {
    moduleType: typeof piexifModule,
    hasDefault: !!(piexifModule && piexifModule.default),
    hasPiexif: !!(piexifModule && piexifModule.piexif),
    resolvedType: typeof piexif,
    hasLoad: !!(piexif && piexif.load)
});

class AdvancedImageUploader {
  constructor(options = {}) {
    this.options = {
      maxWidth: options.maxWidth || 1920,
      maxHeight: options.maxHeight || 1080,
      quality: options.quality || 0.8,
      targetMaxBytes: options.targetMaxBytes || 1024 * 1024,
      minQuality: options.minQuality || 0.6,
      enableClientResize: options.enableClientResize !== false,
      enableClientThumbnail: options.enableClientThumbnail !== false,
      thumbnailQuality: options.thumbnailQuality || 0.7,
      enableClientVariants: options.enableClientVariants !== false,
      variantSizes: options.variantSizes || [1080, 700, 500, 200],
      variantQuality: options.variantQuality || options.quality || 0.8,
      enableWebP: options.enableWebP && this.supportsWebP(),
      enableHEIC: options.enableHEIC !== false,
      progressCallback: options.progressCallback || null,
      maxFileSize: options.maxFileSize || 10 * 1024 * 1024, // 10MB
      concurrentUploads: options.concurrentUploads || 1, // デフォルトを1（直列）に変更してサーバー側の競合を回避
      ...options
    };

    this.uploadQueue = [];
    this.activeUploads = 0;
    this.totalFiles = 0;
    this.completedFiles = 0;
  }

  /**
   * WebPサポートを確認
   */
  supportsWebP() {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 1;
    return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  }

  /**
   * HEICファイルかどうかを確認
   */
  isHEIC(file) {
    const heicExtensions = ['.heic', '.heif', '.HEIC', '.HEIF'];
    return heicExtensions.some(ext => file.name.toLowerCase().endsWith(ext.toLowerCase())) ||
           file.type === 'image/heic' || file.type === 'image/heif';
  }

  /**
   * メインアップロード処理
   */
  async uploadFiles(files, uploadOptions = {}) {
    this.totalFiles = files.length;
    this.completedFiles = 0;
    
    // 進捗初期化
    this.updateProgress(0, 'ファイルを準備中...');

    try {
      // ファイルを検証・最適化してキューに追加
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // ファイル検証
        if (!this.validateFile(file)) {
          continue;
        }

        // キューに追加
        this.uploadQueue.push({
          originalFile: file,
          index: i,
          uploadOptions
        });
      }

      // 並列アップロード開始
      const results = await this.processUploadQueue();
      
      this.updateProgress(100, 'アップロード完了！');
      return results;

    } catch (error) {
      console.error('アップロードエラー:', error);
      throw error;
    }
  }

  /**
   * ファイル検証
   */
  validateFile(file) {
    // サイズチェック
    if (file.size > this.options.maxFileSize) {
      console.warn(`ファイルサイズが大きすぎます: ${file.name}`);
      return false;
    }

    // 画像ファイルかチェック
    if (!file.type.startsWith('image/') && !this.isHEIC(file)) {
      console.warn(`画像ファイルではありません: ${file.name}`);
      return false;
    }

    return true;
  }

  /**
   * アップロードキューの処理
   */
  async processUploadQueue() {
    return new Promise((resolve) => {
      const results = [];
      let processedCount = 0;

      const processNext = async () => {
        if (this.uploadQueue.length === 0) {
          if (processedCount === this.totalFiles) {
            resolve(results);
          }
          return;
        }

        if (this.activeUploads >= this.options.concurrentUploads) {
          return;
        }

        const item = this.uploadQueue.shift();
        this.activeUploads++;

        try {
          const result = await this.processAndUploadFile(item);
          results[item.index] = result;
        } catch (error) {
          console.error(`ファイル処理エラー:`, error);
          results[item.index] = { error: error.message };
        }

        this.activeUploads--;
        processedCount++;
        this.completedFiles = processedCount;

        // 進捗更新
        const progress = (processedCount / this.totalFiles) * 100;
        this.updateProgress(progress, `${processedCount}/${this.totalFiles} ファイル完了`);

        // 次のファイルを処理
        setTimeout(processNext, 10);
      };

      // 同時実行数分だけ開始
      for (let i = 0; i < this.options.concurrentUploads; i++) {
        processNext();
      }
    });
  }

  /**
   * 個別ファイルの処理とアップロード
   */
  async processAndUploadFile(item) {
    const { originalFile, uploadOptions } = item;
    const normalizedOptions = { ...(uploadOptions || {}) };
    if (!normalizedOptions.client_image_id) {
      normalizedOptions.client_image_id = this._generateClientImageId();
    }
    
    let processedFile = originalFile;
    let thumbnailFile = null;
    let variantFiles = [];

    try {
      // 1. HEIC変換（必要な場合）
      if (this.isHEIC(originalFile)) {
        this.updateProgress(null, `HEIC変換中: ${originalFile.name}`);
        processedFile = await this.convertHEICToJPEG(originalFile, normalizedOptions);
        if (processedFile && processedFile.serverUploadResult) {
          const serverResult = processedFile.serverUploadResult || {};
          const uploadResult = serverResult.upload_result || {};
          return {
            success: !!serverResult.success,
            server_upload: true,
            original: originalFile.name,
            processed: serverResult.filename || originalFile.name,
            code: uploadResult.code,
            fileids: uploadResult.fileids,
            ...serverResult
          };
        }
      }

      // 2. クライアントサイド圧縮（必要な場合）
      if (this.options.enableClientResize) {
        this.updateProgress(null, `画像最適化中: ${processedFile.name}`);
        processedFile = await this.optimizeImage(processedFile);
      }

      // 2.5. サムネイル生成（必要な場合）
      if (this.options.enableClientThumbnail && processedFile.type.startsWith('image/')) {
        try {
          thumbnailFile = await this.createThumbnailFile(processedFile);
        } catch (thumbError) {
          console.warn('サムネイル生成に失敗しました（無視して続行）:', thumbError);
        }
      }

      // 2.6. リサイズバリアント生成（必要な場合）
      if (this.options.enableClientVariants && processedFile.type.startsWith('image/')) {
        try {
          variantFiles = await this.createVariantFiles(processedFile);
        } catch (variantError) {
          console.warn('リサイズ画像の生成に失敗しました（無視して続行）:', variantError);
        }
      }

      // 3. アップロード実行
      this.updateProgress(null, `アップロード中: ${processedFile.name}`);
      const extraFiles = [];
      if (thumbnailFile) {
        extraFiles.push({ file: thumbnailFile, isThumbnail: true, mirrorMeta: true });
      }
      if (variantFiles.length) {
        variantFiles.forEach((variant) => {
          extraFiles.push({
            file: variant.file,
            size: variant.size,
            isResized: true,
            mirrorMeta: true
          });
        });
      }
      const result = await this.uploadSingleFile(processedFile, normalizedOptions, extraFiles);

      return {
        success: true,
        original: originalFile.name,
        processed: processedFile.name,
        ...result
      };

    } catch (error) {
      console.error(`ファイル処理エラー: ${originalFile.name}`, error);
      return {
        success: false,
        error: error.message,
        original: originalFile.name
      };
    }
  }

  /**
   * HEIC → JPEG変換
   */
  async convertHEICToJPEG(heicFile, uploadOptions = {}) {
    // heicSupportを使用して変換（サーバーフォールバック付き）
    if (window.heicSupport) {
      try {
        return await window.heicSupport.convertToJPEGWithFallback(heicFile, {
          quality: this.options.quality,
          serverUpload: true,
          uploadOptions: uploadOptions,
          webUploaderUrl: uploadOptions.web_uploader_url
        });
      } catch (error) {
        console.error('HEICSupportによる変換に失敗しました:', error);
        throw new Error(`HEIC変換に失敗しました: ${heicFile.name} (${error.message})`);
      }
    }
    
    throw new Error('HEIC変換ライブラリがロードされていません。');
  }

  /**
   * 画像最適化（リサイズ・圧縮）
   */
  async optimizeImage(file) {
    // HEICのままの場合はスキップ（Canvasで読み込めないため）
    if (this.isHEIC(file)) {
      console.warn('HEICファイルはリサイズをスキップします (変換失敗の可能性があります):', file.name);
      return file;
    }

    // WebPの場合はEXIF保持が難しいため、現状はスキップまたはJPEGのみ対応
    if (this.options.enableWebP || (file.type !== 'image/jpeg' && file.type !== 'image/jpg')) {
      return this._optimizeImageInternal(file);
    }

    try {
      // 1. 元ファイルからEXIFを取得（HEIC由来のEXIFがある場合はそれを優先）
      let exifData = null;
      let exifStr = null;
      let exifBinary = null;
      try {
        if (window.__heicExifMap && file) {
          const payload = window.__heicExifMap.get(file);
          if (payload && typeof payload === 'object') {
            exifBinary = payload.exifBinary || null;
            exifStr = payload.exifStr || null;
          } else {
            exifBinary = payload || null;
          }
        }
      } catch (e) {
        exifBinary = null;
      }
      if (!exifBinary && !exifStr && file && file._exifBinary) {
        exifBinary = file._exifBinary;
      }
      if (!exifStr && file && file._exifStr) {
        exifStr = file._exifStr;
      }
      if (!exifStr && exifBinary && piexif) {
        exifStr = this._arrayBufferToBinaryString(exifBinary);
        if (exifStr.substring(0, 4) !== 'Exif') {
          exifStr = '\x45\x78\x69\x66\x00\x00' + exifStr;
        }
      }
      if (!exifStr) {
        const originalBinary = await this.fileToBinaryString(file);
        try {
          exifData = piexif.load(originalBinary);
        } catch (e) {
          console.warn('EXIFの読み込みに失敗しました（無視して続行）:', e);
        }
      }

      // 2. 通常の最適化処理
      const optimizedFile = await this._optimizeImageInternal(file);

      // 3. 最適化後のファイルにEXIFを再注入
      if ((exifStr || exifData) && optimizedFile.type === 'image/jpeg') {
        const optimizedBinary = await this.fileToBinaryString(optimizedFile);
        const finalExifStr = exifStr || piexif.dump(exifData);
        const insertedBinary = piexif.insert(finalExifStr, optimizedBinary);
        
        // 再びFileオブジェクトに戻す
        const outputFile = this.binaryStringToFile(insertedBinary, optimizedFile.name, optimizedFile.type);
        if (exifBinary || exifStr) {
          try {
            if (window.__heicExifMap) {
              window.__heicExifMap.set(outputFile, { exifBinary, exifStr });
            }
          } catch (e) {
            if (exifBinary) {
              outputFile._exifBinary = exifBinary;
            } else if (exifStr) {
              outputFile._exifStr = exifStr;
            }
          }
        }
        return outputFile;
      }

      return optimizedFile;
    } catch (error) {
      console.error('画像最適化中のEXIF保持エラー:', error);
      return file;
    }
  }

  /**
   * 実際の画像最適化処理（リサイズ・圧縮）
   */
  async _optimizeImageInternal(file) {
    const img = await this._loadImage(file);
    if (!img) {
      return file;
    }

    // 新しいサイズを計算
    let { width, height } = this.calculateNewDimensions(
      img.width,
      img.height
    );

    // 出力形式を決定
    const outputFormat = this.options.enableWebP ? 'image/webp' : file.type;
    const targetMaxBytes = this.options.targetMaxBytes;
    const shouldTargetSize = targetMaxBytes && file.size > targetMaxBytes;
    let quality = this.options.quality;
    let bestBlob = null;

    for (let attempt = 0; attempt < 6; attempt++) {
      const blob = await this._renderToBlob(img, width, height, outputFormat, quality);
      if (!blob) {
        break;
      }

      if (!bestBlob || blob.size < bestBlob.size) {
        bestBlob = blob;
      }

      if (!shouldTargetSize || blob.size <= targetMaxBytes) {
        bestBlob = blob;
        break;
      }

      if (quality > this.options.minQuality + 0.01) {
        quality = Math.max(this.options.minQuality, quality - 0.1);
        continue;
      }

      const scale = Math.sqrt(targetMaxBytes / blob.size);
      if (scale >= 0.95) {
        break;
      }

      width = Math.max(1, Math.floor(width * scale));
      height = Math.max(1, Math.floor(height * scale));
    }

    if (!bestBlob) {
      return file;
    }

    const shouldUseOptimized = this.options.enableWebP ||
      bestBlob.size < file.size ||
      (shouldTargetSize && bestBlob.size <= targetMaxBytes);

    if (!shouldUseOptimized) {
      return file;
    }

    const extension = outputFormat === 'image/webp' ? '.webp' :
      outputFormat === 'image/jpeg' ? '.jpg' :
      outputFormat === 'image/png' ? '.png' : '.jpg';

    return new File([bestBlob],
      file.name.replace(/\.[^/.]+$/, extension),
      {
        type: outputFormat,
        lastModified: Date.now()
      }
    );
  }

  _loadImage(file) {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };

      img.src = url;
    });
  }

  _renderToBlob(img, width, height, outputFormat, outputQuality) {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(null);
        return;
      }

      canvas.width = width;
      canvas.height = height;

      // 高品質でリサイズ
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => resolve(blob),
        outputFormat,
        outputQuality
      );
    });
  }

  async createThumbnailFile(file) {
    const img = await this._loadImage(file);
    if (!img) {
      return null;
    }

    const width = Math.max(1, Math.round(img.width / 2));
    const height = Math.max(1, Math.round(img.height / 2));
    const outputFormat = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
    const outputQuality = outputFormat === 'image/jpeg' ? this.options.thumbnailQuality : undefined;
    const blob = await this._renderToBlob(img, width, height, outputFormat, outputQuality);
    if (!blob) {
      return null;
    }

    const baseName = file.name.replace(/\.[^/.]+$/, '');
    const extension = outputFormat === 'image/png' ? '.png' : '.jpg';
    return new File([blob], `${baseName}_thumb${extension}`, {
      type: outputFormat,
      lastModified: Date.now()
    });
  }

  async createVariantFiles(file) {
    const img = await this._loadImage(file);
    if (!img) {
      return [];
    }

    const originalMax = Math.max(img.width, img.height);
    const outputFormat = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
    const outputQuality = outputFormat === 'image/jpeg' ? this.options.variantQuality : undefined;
    const baseName = file.name.replace(/\.[^/.]+$/, '');
    const extension = outputFormat === 'image/png' ? '.png' : '.jpg';
    const results = [];

    for (const size of this.options.variantSizes) {
      if (originalMax <= size) {
        continue;
      }
      const { width, height } = this._fitToSize(img.width, img.height, size);
      const blob = await this._renderToBlob(img, width, height, outputFormat, outputQuality);
      if (!blob) {
        continue;
      }
      results.push({
        size,
        file: new File([blob], `${baseName}_size${size}${extension}`, {
          type: outputFormat,
          lastModified: Date.now()
        })
      });
    }

    return results;
  }

  _fitToSize(width, height, maxSize) {
    const scale = Math.min(1, maxSize / Math.max(width, height));
    return {
      width: Math.max(1, Math.round(width * scale)),
      height: Math.max(1, Math.round(height * scale))
    };
  }

  /**
   * File/Blobをバイナリ文字列に変換
   */
  async fileToBinaryString(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsBinaryString(file);
    });
  }

  _arrayBufferToBinaryString(buffer) {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return binary;
  }

  /**
   * バイナリ文字列をFileオブジェクトに変換
   */
  binaryStringToFile(binaryString, fileName, mimeType) {
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return new File([bytes], fileName, { type: mimeType });
  }

  /**
   * 新しい画像サイズを計算
   */
  calculateNewDimensions(originalWidth, originalHeight) {
    let { width, height } = { width: originalWidth, height: originalHeight };

    if (width > this.options.maxWidth) {
      height = (height * this.options.maxWidth) / width;
      width = this.options.maxWidth;
    }

    if (height > this.options.maxHeight) {
      width = (width * this.options.maxHeight) / height;
      height = this.options.maxHeight;
    }

    return { width: Math.round(width), height: Math.round(height) };
  }

  /**
   * 単一ファイルのアップロード
   */
  async uploadSingleFile(file, options = {}, extraFiles = []) {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      try {
        if (file && file.type === 'image/jpeg' && piexif) {
          const reader = new FileReader();
          reader.onload = (e) => {
            try {
              const binary = e.target.result;
              const exif = piexif.load(binary);
              const gps = exif && exif.GPS ? exif.GPS : null;
              console.log('[UPLOAD] EXIF GPS (pre-upload):', {
                hasGps: !!gps && Object.keys(gps).length > 0,
                gpsKeys: gps ? Object.keys(gps) : []
              });
            } catch (exifErr) {
              console.warn('[UPLOAD] EXIF GPS read failed (pre-upload):', exifErr);
            }
          };
          reader.readAsBinaryString(file);
        }
      } catch (e) {
        console.warn('[UPLOAD] EXIF GPS log skipped:', e);
      }
      formData.append('pictures[0]', file);
      formData.append('app_session_id', options.app_session_id || '');
      formData.append('checkSpot', options.checkSpot || '');
      
      // その他のオプションを追加
      Object.entries(options).forEach(([key, value]) => {
        if (key !== 'app_session_id' && key !== 'checkSpot') {
          formData.append(key, value);
        }
      });

      extraFiles.forEach((item, index) => {
        const targetIndex = index + 1;
        formData.append(`pictures[${targetIndex}]`, item.file);
        if (item.isThumbnail) {
          formData.append(`pictures[${targetIndex}][is_thumbnail]`, '1');
        }
        if (item.isResized) {
          formData.append(`pictures[${targetIndex}][is_resized]`, '1');
        }
        if (item.size) {
          formData.append(`pictures[${targetIndex}][size]`, String(item.size));
        }
        if (item.mirrorMeta) {
          this._mirrorPictureMeta(options, formData, targetIndex);
        }
      });

      const xhr = new XMLHttpRequest();

      // プログレス監視
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          this.updateProgress(null, `${file.name}: ${Math.round(percentComplete)}%`);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.code === 'S0000') {
              resolve(response);
            } else {
              reject(new Error(`アップロードエラー: ${response.description || 'Unknown error'}`));
            }
          } catch (error) {
            reject(new Error('レスポンスの解析に失敗しました'));
          }
        } else {
          reject(new Error(`アップロードに失敗しました: ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('ネットワークエラーが発生しました'));
      });

      // URLの決定
      // 最も安全で確実な方法は、現在のオリジンを使用することです。
      // これにより、localhostでもAWSでも、常に正しいサーバー（PHPが動いているサーバー）にリクエストが飛びます。
      
      // 相対パスを使用すれば、ブラウザが自動的に現在のスキームとドメインを補完してくれます
      const uploadPath = '/img_converter/web_uploader.php';
      
      // ただし、もしAPIドメインが明示的に別ドメイン（CORS環境など）を指している場合は考慮が必要ですが、
      // 今回の構成（同一サーバー上のPHP）であれば、現在地のオリジンを使うのがベストプラクティスです。
      
      let uploadUrl = uploadPath;
      
      // apiドメインが設定されており、かつ現在のオリジンと異なる場合（クロスドメイン）のみ、フルURLを構築
      if (window.AS && window.AS.domains && window.AS.domains.api) {
          const apiDomain = window.AS.domains.api;
          // APIドメインにポート3000が含まれている場合は除外して使う（PHPへのリクエストのため）
          if (apiDomain.includes(':3000')) {
               // ポート3000を除去するより、現在のオリジン（ポートなし/80/443）を使ったほうが安全
               uploadUrl = window.location.origin + uploadPath;
          } else if (!apiDomain.includes(window.location.hostname)) {
              // 全く別のドメインの場合
              uploadUrl = apiDomain + uploadPath;
          } else {
              // 同一ホストなら相対パスまたはオリジン付きで
              uploadUrl = window.location.origin + uploadPath;
          }
      } else {
          // デフォルトは現在のオリジンを使用
           uploadUrl = window.location.origin + uploadPath;
      }

  xhr.open('POST', uploadUrl);
  xhr.send(formData);
    });
  }

  _generateClientImageId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    const rand = Math.random().toString(16).slice(2);
    return `img_${Date.now().toString(16)}_${rand}`;
  }

  _mirrorPictureMeta(options, formData, targetIndex) {
    Object.entries(options).forEach(([key, value]) => {
      const match = key.match(/^pictures\\[0\\]\\[(.+)\\]$/);
      if (match) {
        formData.append(`pictures[${targetIndex}][${match[1]}]`, value);
      }
    });
  }

  /**
   * 進捗更新
   */
  updateProgress(percentage, message) {
    if (this.options.progressCallback) {
      this.options.progressCallback({
        percentage: percentage,
        message: message,
        completed: this.completedFiles,
        total: this.totalFiles
      });
    }
  }
}

// MapLife用のグローバル関数として登録
window.AdvancedImageUploader = AdvancedImageUploader;

export default AdvancedImageUploader;
